import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, 
  Shield, 
  ShieldCheck, 
  ShieldX,
  Key,
  Settings,
  Upload,
  Edit,
  Trash,
  Eye,
  Download,
  UserCog
} from 'lucide-react';
import { Layout } from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Permission icons mapping
const permissionIcons = {
  upload_sales: Upload,
  upload_returns: Upload,
  upload_products: Upload,
  upload_combos: Upload,
  upload_sku_mappings: Upload,
  upload_warehouse_stock: Upload,
  edit_products: Edit,
  edit_inventory: Edit,
  edit_combos: Edit,
  edit_sku_mappings: Edit,
  edit_warehouse_stock: Edit,
  delete_products: Trash,
  delete_combos: Trash,
  delete_data: Trash,
  manage_suppliers: Settings,
  manage_settings: Settings,
  manage_users: UserCog,
  view_forecast: Eye,
  view_reorder: Eye,
  export_data: Download,
};

export default function UserManagementPage() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAvailablePermissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/permissions/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailablePermissions(data.permissions);
        setGroupedPermissions(data.grouped);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (res.ok) {
        toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const openPermissionsDialog = (user) => {
    setSelectedUser(user);
    setEditedPermissions([...user.permissions]);
    setShowPermissionsDialog(true);
  };

  const handlePermissionToggle = (permission) => {
    setEditedPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSelectAllInCategory = (categoryPermissions, select) => {
    const permKeys = Object.keys(categoryPermissions);
    setEditedPermissions(prev => {
      if (select) {
        return [...new Set([...prev, ...permKeys])];
      } else {
        return prev.filter(p => !permKeys.includes(p));
      }
    });
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ permissions: editedPermissions })
      });
      
      if (res.ok) {
        toast.success('Permissions updated successfully');
        setShowPermissionsDialog(false);
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update permissions');
      }
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (res.ok) {
        toast.success('Password reset successfully');
        setShowPasswordDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="p-8 text-center">
          <ShieldX className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">Manage user accounts and permissions</p>
        </div>
        <Badge variant="outline" className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          {users.length} Users
        </Badge>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading users...</div>
      ) : (
        <div className="grid gap-4">
          {users.map(user => (
            <Card key={user.id} className={`${!user.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.role === 'admin' ? 'bg-amber-100' : 'bg-gray-100'
                    }`}>
                      {user.role === 'admin' ? (
                        <ShieldCheck className="w-6 h-6 text-amber-600" />
                      ) : (
                        <Shield className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{user.name}</h3>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                        {!user.is_active && (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {user.permissions?.length || 0} permissions granted
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {user.role !== 'admin' && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Active</span>
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => handleToggleActive(user.id, user.is_active)}
                            data-testid={`toggle-active-${user.email}`}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPermissionsDialog(user)}
                          data-testid={`edit-permissions-${user.email}`}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Permissions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPasswordDialog(user)}
                          data-testid={`reset-password-${user.email}`}
                        >
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </Button>
                      </>
                    )}
                    {user.role === 'admin' && user.id === currentUser?.id && (
                      <span className="text-sm text-gray-400 italic">Current User</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Permissions for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {Object.entries(groupedPermissions).map(([category, permissions]) => (
              <div key={category} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">{category}</h4>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAllInCategory(permissions, true)}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAllInCategory(permissions, false)}
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(permissions).map(([key, label]) => {
                    const Icon = permissionIcons[key] || Shield;
                    const isChecked = editedPermissions.includes(key);
                    const isCritical = key.startsWith('delete_') || key.startsWith('manage_');
                    
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 ${
                          isChecked ? 'bg-gray-50 border-gray-300' : 'border-gray-200'
                        } ${isCritical ? 'border-amber-200' : ''}`}
                        onClick={() => handlePermissionToggle(key)}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handlePermissionToggle(key)}
                        />
                        <Icon className={`w-4 h-4 ${isCritical ? 'text-amber-500' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isCritical ? 'text-amber-700' : 'text-gray-700'}`}>
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={savePermissions} disabled={saving}>
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700">New Password</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              className="mt-1"
              data-testid="new-password-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={resetPassword} disabled={saving || newPassword.length < 6}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
